export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="h-screen max-h-screen overflow-y-auto">
        <div className="px-5 pt-4 pb-9 max-w-[50rem] mx-auto flex flex-col gap-6">
          <h1 className="text-3xl font-black text-text tracking-tight">Privacy Policy</h1>
          <p className="text-text-muted text-base leading-6">Last updated: June 2026</p>

          <p className="text-text text-sm leading-6">
            Footslapping ("we," "our," or "us") operates the website at footslapping.com and the
            Footslapping mobile application (collectively, the "Service"). This Privacy Policy
            explains how we collect, use, disclose, and safeguard your information when you use the
            Service. If you do not agree with the terms of this policy, please do not access the
            Service.
          </p>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">1. Information We Collect</h2>
            <h3 className="text-lg font-semibold text-text">Personal Data</h3>
            <p className="text-text-muted text-sm leading-6">
              Personally identifiable information you voluntarily provide when you register an
              account, participate in competitions, or contact support — such as your name, email
              address, and account credentials.
            </p>
            <h3 className="text-lg font-semibold text-text">Fitness Data</h3>
            <p className="text-text-muted text-sm leading-6">
              With your explicit permission, we collect step count data from connected health
              providers (Apple Health, Google Health) to track competition progress and award
              badges. We do not collect precise geolocation, health records, or other sensitive
              information beyond step counts. You may grant or withdraw permission at any time
              through your device settings.
            </p>
            <h3 className="text-lg font-semibold text-text">Usage & Device Data</h3>
            <p className="text-text-muted text-sm leading-6">
              Information our servers automatically collect when you access the Service, such as
              your IP address, browser type, operating system, access times, and pages viewed.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">2. Cookies & Analytics</h2>
            <p className="text-text-muted text-sm leading-6">
              We use cookies, web beacons, tracking pixels, and similar technologies to customize
              the Service and improve your experience. Third-party analytics tools (such as Google
              Analytics) may collect information sent by your device to help us analyze usage
              patterns and improve the Service.
            </p>
            <p className="text-text-muted text-sm leading-6">
              You can set your browser to refuse cookies or alert you when cookies are being sent.
              The Service respects Do-Not-Track ("DNT") browser signals and will honor your tracking
              preference when a DNT signal is detected.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">3. How We Use Your Information</h2>
            <p className="text-text-muted text-sm leading-6">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-5 text-text-muted text-sm leading-6 flex flex-col gap-1">
              <li>Create and manage your user account and run step competitions.</li>
              <li>Deliver targeted fitness features, services, or application updates.</li>
              <li>Monitor and analyze usage and trends to improve your user experience.</li>
              <li>Respond to customer service requests and support needs.</li>
              <li>
                Prevent fraudulent transactions, monitor against theft, and protect against criminal
                activity.
              </li>
            </ul>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">4. Data Sharing & Disclosure</h2>
            <p className="text-text-muted text-sm leading-6">
              We do not sell, rent, or lease your personal data to third parties. We may share your
              information only in the following circumstances:
            </p>
            <h3 className="text-lg font-semibold text-text">Service Providers</h3>
            <p className="text-text-muted text-sm leading-6">
              We share data with trusted third parties who perform services for us, including cloud
              hosting and infrastructure (Cloudflare), database management (D1), authentication
              platforms, email delivery systems, and analytics providers. These providers are
              contractually bound to protect your data and use it only for the services we request.
            </p>
            <h3 className="text-lg font-semibold text-text">Legal Obligations</h3>
            <p className="text-text-muted text-sm leading-6">
              We may disclose your information if required to do so by law, to respond to legal
              process, to investigate potential violations of our policies, or to protect the
              rights, property, and safety of others.
            </p>
            <h3 className="text-lg font-semibold text-text">Business Transfers</h3>
            <p className="text-text-muted text-sm leading-6">
              If we undergo a merger, acquisition, or sale of assets, your information may be
              transferred to the successor entity as part of that transaction.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">5. International Data Transfers</h2>
            <p className="text-text-muted text-sm leading-6">
              The Service is hosted and operated in the United States. If you access the Service
              from outside the United States, your information will be transferred to, stored, and
              processed in the United States. We ensure appropriate safeguards are in place to
              protect your data in accordance with applicable international privacy regulations.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">6. Data Retention</h2>
            <p className="text-text-muted text-sm leading-6">
              We retain your personal data only as long as your account is active or as needed to
              provide you with the Service. Account data is retained until you delete your account.
              Fitness and competition history is retained as long as necessary to maintain active
              leaderboards, after which it is anonymized or deleted. Aggregated, anonymized usage
              data may be retained indefinitely for statistical and product improvement purposes.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">7. Data Security</h2>
            <p className="text-text-muted text-sm leading-6">
              We use administrative, technical, and physical security measures — including
              industry-standard encryption for data in transit and at rest — to help protect your
              personal information. No method of transmission over the internet is completely
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">8. Children's Privacy</h2>
            <p className="text-text-muted text-sm leading-6">
              We do not knowingly collect information from or market to children under the age of
              13. If you become aware of data we have collected from a child under 13, please
              contact us immediately at support@footslapping.com, and we will take steps to delete
              that information promptly.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">9. Your Privacy Rights</h2>
            <h3 className="text-lg font-semibold text-text">CCPA/CPRA (California Residents)</h3>
            <p className="text-text-muted text-sm leading-6">
              You have the right to know what personal information we collect, to request deletion,
              to request correction of inaccurate information, and to non-discrimination for
              exercising your rights. We do not sell your personal information or share it for
              cross-context behavioral advertising.
            </p>
            <h3 className="text-lg font-semibold text-text">How to Exercise Your Rights</h3>
            <p className="text-text-muted text-sm leading-6">
              Submit a verifiable request to support@footslapping.com. We will take steps to verify
              your identity before fulfilling your request.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">10. Changes to This Policy</h2>
            <p className="text-text-muted text-sm leading-6">
              We reserve the right to update, modify, or revise this Privacy Policy at any time.
              Material changes will be notified to you by email, via an in-app notification, or by a
              prominent notice on the Service prior to the change becoming effective. Non-material
              changes may be made without advance notice. The "Last updated" date at the top of this
              page will reflect the most recent revision.
            </p>
            <p className="text-text-muted text-sm leading-6">
              Your continued use of the Service after any modifications constitutes your acceptance
              of the updated terms. If you do not agree with any changes, you should discontinue use
              and delete your account. We encourage you to periodically review this page for the
              latest information on our privacy practices.
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold text-text">11. Contact Us</h2>
            <p className="text-text-muted text-sm leading-6">
              If you have questions or comments about this Privacy Policy, or wish to exercise your
              data privacy rights, please contact us at:
            </p>
            <p className="text-text text-sm leading-6">
              Email: support@footslapping.com
              <br />
              Website: https://footslapping.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
